// Per-plate lookup session for the details screen.
//
// Navigation happens the moment a plate locks; this module then consults the
// details cache (IndexedDB, see ../vehicle/detailsCache.ts) and fans out only
// the sources that miss. Fresh cached slices render instantly; fetched ones
// stream into reactive slices as they land (soov car-data ~600 ms, mnt ~5 s,
// auto24 ~20 s) and are written back for the next scan — even when the user
// has already left the screen: requests are shared per plate+source and
// finish in the background instead of being cancelled on dispose. Nothing
// blocks: each card on the details screen watches its own slice.
//
//   plate ──▶ cache ──┬─ /car-data ──▶ carData ──▶ AI summary (deferred, cached per language)
//                     ├─ /mnt ───────▶ mnt (registry backbone)
//                     ├─ /car-tax ───▶ tax
//                     └─ /auto24-price ▶ price

import { reactive, watch, type WatchStopHandle } from 'vue';
import { lookupVehicle, sanitizeRegistration, type MntResponse } from '../api/checkVehicle';
import { fetchCarData, type CarDataResponse } from '../api/carData';
import { extractCarTax, fetchCarTax, type CarTaxResponse } from '../api/carTax';
import { fetchAuto24Price, type Auto24PriceResponse } from '../api/auto24Price';
import { fetchLatvianRegCheck, type LatvianRegCheckResponse } from '../api/latvianRegCheck';
import {
  aiInputFromCarData,
  aiInputFromMnt,
  aiSummaryConfigured,
  fetchAiFuelEconomy,
  fetchAiSummary,
  type AiCarInsights,
  type AiFuelFigures,
  type AiFuelNeed,
} from '../api/aiSummary';
import { readVehicleCache, writeVehicleCache, type VehicleCachePatch } from '../vehicle/detailsCache';
import { i18n } from '../i18n';

export type SourceStatus = 'idle' | 'loading' | 'success' | 'error';

export interface SourceState<T> {
  status: SourceStatus;
  data: T | null;
  error: string | null;
}

export interface VehicleLookupSession {
  plate: string;
  region?: string;
  isLatvian: boolean;
  mnt: SourceState<MntResponse>;
  latvian: SourceState<LatvianRegCheckResponse>;
  carData: SourceState<CarDataResponse>;
  tax: SourceState<CarTaxResponse>;
  price: SourceState<Auto24PriceResponse>;
  ai: SourceState<AiCarInsights>;
  aiFuel: SourceState<AiFuelFigures>;
  retryMnt: () => void;
  dispose: () => void;
}

function initial<T>(status: SourceStatus = 'loading'): SourceState<T> {
  return { status, data: null, error: null };
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// One in-flight request per plate+source across sessions. Leaving the details
// screen does not cancel anything: a slow source (auto24 ~20 s) finishes in
// the background and lands in the cache, and a re-scan of the same plate
// attaches to the running request instead of restarting it.
const inflight = new Map<string, Promise<unknown>>();

function shared<T>(key: string, make: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;
  const request = make().finally(() => inflight.delete(key));
  inflight.set(key, request);
  return request;
}

export function startVehicleLookup(rawPlate: string): VehicleLookupSession {
  return startVehicleLookupWithRegion(rawPlate);
}

export function startVehicleLookupWithRegion(rawPlate: string, region?: string): VehicleLookupSession {
  const plate = sanitizeRegistration(rawPlate);
  const normalizedRegion = region?.trim() || undefined;
  const isLatvian = normalizedRegion === 'Latvia';
  let disposed = false;
  let aiSeq = 0;
  let stopLocaleWatch: WatchStopHandle | null = null;
  // AI summaries per language: from the cache plus any fetched this session,
  // so switching back to an already-seen language swaps instantly.
  const aiByLang = new Map<string, AiCarInsights>();

  const session: VehicleLookupSession = reactive({
    plate,
    ...(normalizedRegion ? { region: normalizedRegion } : {}),
    isLatvian,
    mnt: initial<MntResponse>(),
    latvian: initial<LatvianRegCheckResponse>(isLatvian ? 'loading' : 'idle'),
    carData: initial<CarDataResponse>(),
    tax: initial<CarTaxResponse>(),
    price: initial<Auto24PriceResponse>(),
    ai: initial<AiCarInsights>('idle'),
    aiFuel: initial<AiFuelFigures>('idle'),
    retryMnt,
    dispose,
  });

  // The cache is best-effort: a failed write must never surface in the UI.
  function persist(patch: VehicleCachePatch): void {
    writeVehicleCache(plate, patch).catch(() => {});
  }

  function run<T>(
    slice: SourceState<T>,
    request: () => Promise<T>,
    opts: { cache?: (data: T) => void; onSuccess?: (data: T) => void; onSettled?: () => void } = {},
  ): void {
    slice.status = 'loading';
    slice.error = null;
    request().then(
      (data) => {
        // A result landing after the user left is still worth caching.
        opts.cache?.(data);
        if (disposed) return;
        slice.data = data;
        slice.status = 'success';
        opts.onSuccess?.(data);
        opts.onSettled?.();
      },
      (err) => {
        if (disposed) return;
        slice.status = 'error';
        slice.error = errorText(err);
        opts.onSettled?.();
      },
    );
  }

  function serveFromCache<T>(slice: SourceState<T>, data: T): void {
    slice.data = data;
    slice.status = 'success';
    slice.error = null;
  }

  function fetchLatvianSource(): void {
    run(session.latvian, () => shared(`${plate}:latvian`, () => fetchLatvianRegCheck(plate)), {
      cache: (data) => persist({ latvian: { data, fetchedAt: Date.now() } }),
    });
  }

  // Only substantive results are cached: transient upstream hiccups (empty
  // car-data, missing price, mnt not_found) should retry on the next scan
  // rather than freeze a blank card for the whole TTL.
  function fetchCarDataSource(): void {
    run(session.carData, () => shared(`${plate}:carData`, () => fetchCarData(plate)), {
      cache: (data) => {
        if (data.dynamic || data.static) persist({ carData: { data, fetchedAt: Date.now() } });
      },
      onSuccess: () => runAiSummary(),
      onSettled: () => maybeRunAiFuel(),
    });
  }

  function fetchMntSource(): void {
    run(session.mnt, () => shared(`${plate}:mnt`, () => lookupVehicle(plate)), {
      cache: (data) => {
        if (data.status === 'ok') persist({ mnt: { data, fetchedAt: Date.now() } });
      },
      onSettled: () => {
        // /car-data is best-effort and can fail independently. The MNT document
        // contains enough make/model/engine data to drive the summary prompt.
        runAiSummary();
        maybeRunAiFuel();
      },
    });
  }

  function fetchTaxSource(): void {
    run(session.tax, () => shared(`${plate}:tax`, () => fetchCarTax(plate)), {
      cache: (data) => {
        const figures = extractCarTax(data);
        if (figures.annualTax !== null || figures.registrationTax !== null) {
          persist({ tax: { data, fetchedAt: Date.now() } });
        }
      },
    });
  }

  function fetchPriceSource(): void {
    run(session.price, () => shared(`${plate}:price`, () => fetchAuto24Price(plate)), {
      cache: (data) => {
        if (data.average_price?.trim()) persist({ price: { data, fetchedAt: Date.now() } });
      },
    });
  }

  function runAiSummary(): void {
    if (disposed) return;
    const lang = i18n.global.locale.value;
    const known = aiByLang.get(lang);
    if (known) {
      aiSeq += 1; // a fetch for a previous language may still be in flight
      serveFromCache(session.ai, known);
      return;
    }
    if (!aiSummaryConfigured()) return;
    const input =
      (session.carData.status === 'success' && session.carData.data
        ? aiInputFromCarData(session.carData.data)
        : null) ??
      (session.mnt.status === 'success' && session.mnt.data
        ? aiInputFromMnt(session.mnt.data)
        : null);
    if (!input) return;

    const seq = ++aiSeq;
    session.ai.status = 'loading';
    session.ai.error = null;
    shared(`${plate}:ai:${lang}`, () => fetchAiSummary(input, lang)).then(
      (insights) => {
        // Cache even a result nobody is waiting for anymore (language switched
        // mid-fetch, or the user left): the tokens are already paid for and it
        // makes the next request for this language instant.
        aiByLang.set(lang, insights);
        persist({ ai: { [lang]: { data: insights, fetchedAt: Date.now() } } });
        if (disposed || seq !== aiSeq) return;
        session.ai.data = insights;
        session.ai.status = 'success';
      },
      (err) => {
        if (disposed || seq !== aiSeq) return;
        session.ai.status = 'error';
        session.ai.error = errorText(err);
      },
    );
  }

  // ---- realtime AI fuel economy ----
  //
  // Fired once BOTH /car-data and /mnt have settled (success or error) and at
  // least one fuel-economy figure is still missing from both responses. Asks
  // the model only for the missing figures; numbers are language-independent,
  // so the result is cached once per plate.

  function settledSource(status: SourceStatus): boolean {
    return status === 'success' || status === 'error';
  }

  function hasCarDataFuel(key: 'KYTUSEKULU_KESK' | 'KYTUSEKULU_LINNAS' | 'KYTUSEKULU_TEEL'): boolean {
    return Boolean(session.carData.data?.static?.[key]?.value?.trim());
  }

  function maybeRunAiFuel(): void {
    if (disposed || isLatvian) return;
    if (session.aiFuel.status !== 'idle') return; // already served or started
    if (!settledSource(session.carData.status) || !settledSource(session.mnt.status)) return;

    const mnt = session.mnt.data;
    const consumption = mnt?.status === 'ok' ? mnt.vehicle.technical_details.fuel_consumption : null;
    const need: AiFuelNeed = {
      combined:
        !consumption?.combined_nedc.raw?.trim() &&
        !consumption?.combined_wltp.raw?.trim() &&
        !hasCarDataFuel('KYTUSEKULU_KESK'),
      city: !consumption?.city.raw?.trim() && !hasCarDataFuel('KYTUSEKULU_LINNAS'),
      highway: !consumption?.highway.raw?.trim() && !hasCarDataFuel('KYTUSEKULU_TEEL'),
    };
    if (!need.combined && !need.city && !need.highway) return;
    if (!aiSummaryConfigured()) return;

    const input =
      (session.carData.data ? aiInputFromCarData(session.carData.data) : null) ??
      (mnt ? aiInputFromMnt(mnt) : null);
    if (!input) return; // no make/model anywhere — nothing to prompt with

    run(session.aiFuel, () => shared(`${plate}:aiFuel`, () => fetchAiFuelEconomy(input, need)), {
      cache: (data) => {
        if (data.combined || data.city || data.highway) {
          persist({ aiFuel: { data, fetchedAt: Date.now() } });
        }
      },
    });
  }

  function retryMnt(): void {
    if (disposed || session.mnt.status === 'loading') return;
    fetchMntSource();
  }

  // Deliberately does not cancel in-flight requests: their results still
  // land in the cache (see `shared` above), only this session's UI freezes.
  function dispose(): void {
    disposed = true;
    stopLocaleWatch?.();
  }

  // Serve fresh cached slices (the IndexedDB read is a few ms), fan out the
  // rest. A broken cache degrades to fetching everything, exactly as before.
  readVehicleCache(plate)
    .catch(() => null)
    .then((cached) => {
      if (disposed) return;
      for (const [lang, slice] of Object.entries(cached?.ai ?? {})) {
        aiByLang.set(lang, slice.data);
      }
      if (isLatvian) {
        if (cached?.latvian) serveFromCache(session.latvian, cached.latvian.data);
        else fetchLatvianSource();
        session.mnt.status = 'idle';
        session.carData.status = 'idle';
        session.tax.status = 'idle';
        session.price.status = 'idle';
        session.ai.status = 'idle';
        return;
      }
      if (cached?.aiFuel) serveFromCache(session.aiFuel, cached.aiFuel.data);
      if (cached?.carData) serveFromCache(session.carData, cached.carData.data);
      else fetchCarDataSource();
      if (cached?.mnt) serveFromCache(session.mnt, cached.mnt.data);
      else fetchMntSource();
      if (cached?.tax) serveFromCache(session.tax, cached.tax.data);
      else fetchTaxSource();
      if (cached?.price) serveFromCache(session.price, cached.price.data);
      else fetchPriceSource();
      // Serves the cached summary for the current language when there is one;
      // otherwise starts a fetch if either car-data or MNT is already present.
      // Each source's completion callback retries this selection, so either one
      // can provide the prompt input when the other source fails.
      runAiSummary();
      // Both sources may have come straight from the cache, in which case no
      // onSettled callback will ever fire — check for missing fuel now too.
      maybeRunAiFuel();
    });

  // The AI summary text is language-specific: swap to the cached translation
  // when the user switches language, or fetch it on first use.
  stopLocaleWatch = watch(i18n.global.locale, () => runAiSummary());

  return session;
}
